import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountLocationView, areLocationsEqual } from '../../add-account-location/models/account-location-view.model';
import { LocationInfoService } from '../services/location-info.service';

@Component({
    providers: [LocationInfoService],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ndt-account-locations',
    styleUrls: ['./account-locations.component.scss'],
    templateUrl: './account-locations.component.html',
    standalone: false
})
export class AccountLocationsComponent implements ControlValueAccessor, OnDestroy {
   @Output() onEditClicked = new EventEmitter<AccountLocationView>();

   locationItems: AccountLocationView[];
   disabled = false;
   selectedLocation: string | null;
   private destroyed$ = new Subject<void>();
   onTouched: () => void;
   private onChange: (value: Array<AccountLocationView> | null) => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private locationInfoService: LocationInfoService
   ) {
      control.valueAccessor = this;
      this.locationInfoService.outsidePointerEvent$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         setTimeout(() => {
            this.selectedLocation = null;
            this.changeDetectorRef.markForCheck();
         }, 0);
      });
   }

   writeValue(locationItems: AccountLocationView[] | null): void {
      if (locationItems) {
         this.locationItems = locationItems.sort((location1, location2) => {
            if (!location1.houseNumber && !location2.houseNumber) {
               return 0;
            }

            if (!location1.houseNumber || location1.houseNumber > (location2.houseNumber ?? '')) {
               return 1;
            }

            if (!location2.houseNumber || location1.houseNumber < location2.houseNumber) {
               return -1;
            }

            return 0;
         });
      }
      this.changeDetectorRef.markForCheck();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   registerOnChange(onChange: (value: AccountLocationView[] | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState(disabled: boolean): void {
      this.disabled = disabled;
      this.changeDetectorRef.markForCheck();
   }

   onDetailsClick(location: AccountLocationView, button: HTMLElement): void {
      if (!this.selectedLocation) {
         this.locationInfoService.showLocationInfo(location, button, this.changeDetectorRef);
         setTimeout(() => {
            this.selectedLocation = location.houseNumber ?? null;
            this.changeDetectorRef.markForCheck();
         }, 0);
      }
   }

   onRemoveClick(location: AccountLocationView) {
      const index = this.locationItems.findIndex((l) => {
         return areLocationsEqual(location, l);
      });

      if (index > -1) {
         this.locationItems.splice(index, 1);
      }

      this.onChange(this.locationItems);
      this.changeDetectorRef.detectChanges();
   }

   onEditClick(location: AccountLocationView) {
      this.onEditClicked.emit(location);
   }
}
